/**
 * Form import/export — pure core.
 *
 * Runtime-dependency free (no Supabase, no React) so it can be unit-tested and
 * imported from worker contexts. The envelope shape produced here is the
 * on-disk format users download and re-upload. Versioned via
 * `bluedatalake_form_export` so future readers can detect/refuse newer files.
 */

import type { FormDefinition, LocalizedString } from './form-builder-types'

/** Bump when the envelope shape changes in a backward-incompatible way. */
export const EXPORT_SCHEMA_VERSION = 1

export type ExportScope = 'project' | 'group' | 'form'
export type ExportSource = 'draft' | 'published'
/** Which definition actually ended up in the envelope for a given form. */
export type DefinitionSource = 'draft' | 'published' | 'empty'

export interface ExportedForm {
  form_key: string
  title: LocalizedString
  description: LocalizedString | null
  icon: string | null
  instance_type: 'single' | 'repeatable'
  required_for_final_submit: boolean
  minimum_instances: number
  instance_label_field_key: string | null
  display_order: number
  /** Honest record of where the definition came from (may differ from the
   *  requested source when the requested one didn't exist). */
  definition_source: DefinitionSource
  /** e.g. "1.0" when the definition was pulled from a published version. */
  source_version_label: string | null
  definition: FormDefinition
}

export interface ExportedGroup {
  title: LocalizedString
  description: LocalizedString | null
  icon: string | null
  display_order: number
  forms: ExportedForm[]
}

export interface FormExportEnvelope {
  /** Envelope schema version (see EXPORT_SCHEMA_VERSION). */
  bluedatalake_form_export: number
  exported_at: string
  scope: ExportScope
  requested_source: ExportSource
  project: { id: string; title: LocalizedString | null }
  groups: ExportedGroup[]
}

// ─── Validation ────────────────────────────────────────────────────────────

export type ValidateResult =
  | { ok: true; envelope: FormExportEnvelope }
  | { ok: false; error: string }

const SCOPES: ExportScope[] = ['project', 'group', 'form']
const INSTANCE_TYPES = ['single', 'repeatable']

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

/**
 * Structurally validate an unknown parsed-JSON value as an export envelope.
 * Lenient on optional/cosmetic fields, strict on the bits `runImport` relies
 * on (scope, form_key, instance_type, a definition with a `fields` array).
 * Definitions are still passed through `parseDefinition` at import time, so we
 * only assert the minimum here.
 */
export function validateEnvelope(raw: unknown): ValidateResult {
  if (!isObject(raw)) return { ok: false, error: 'فایل معتبر نیست: ساختار JSON نادرست است.' }

  const ver = raw.bluedatalake_form_export
  if (typeof ver !== 'number') {
    return { ok: false, error: 'این فایل یک خروجی فرم BlueDataLake نیست.' }
  }
  if (ver > EXPORT_SCHEMA_VERSION) {
    return {
      ok: false,
      error: `این فایل با نسخهٔ جدیدتری ساخته شده (v${ver}). لطفاً برنامه را به‌روزرسانی کنید.`,
    }
  }

  if (!SCOPES.includes(raw.scope as ExportScope)) {
    return { ok: false, error: 'دامنهٔ خروجی نامعتبر است.' }
  }

  if (!Array.isArray(raw.groups)) {
    return { ok: false, error: 'فایل فاقد فهرست گروه‌هاست.' }
  }

  for (const [gi, g] of (raw.groups as unknown[]).entries()) {
    if (!isObject(g)) return { ok: false, error: `گروه #${gi + 1} نامعتبر است.` }
    if (!Array.isArray(g.forms)) {
      return { ok: false, error: `گروه #${gi + 1} فاقد فهرست فرم‌هاست.` }
    }
    for (const [fi, f] of (g.forms as unknown[]).entries()) {
      if (!isObject(f)) return { ok: false, error: `فرم #${fi + 1} در گروه #${gi + 1} نامعتبر است.` }
      if (typeof f.form_key !== 'string' || !f.form_key.trim()) {
        return { ok: false, error: `فرمی در گروه #${gi + 1} فاقد form_key است.` }
      }
      if (!INSTANCE_TYPES.includes(f.instance_type as string)) {
        return { ok: false, error: `فرم «${f.form_key}» نوع نمونهٔ نامعتبری دارد.` }
      }
      if (!isObject(f.definition) || !Array.isArray((f.definition as Record<string, unknown>).fields)) {
        return { ok: false, error: `تعریف فرم «${f.form_key}» نامعتبر است.` }
      }
    }
  }

  // Empty export is structurally valid but useless — flag it.
  const totalForms = (raw.groups as ExportedGroup[]).reduce((n, g) => n + g.forms.length, 0)
  if (totalForms === 0) {
    return { ok: false, error: 'این فایل هیچ فرمی برای ورود ندارد.' }
  }

  return { ok: true, envelope: raw as unknown as FormExportEnvelope }
}

// ─── form_key de-duplication ─────────────────────────────────────────────────

/**
 * Return a form_key unique against `taken`, appending `_2`, `_3`, … on
 * collision. Mutates nothing; caller is responsible for adding the result to
 * its own taken-set before the next call.
 */
export function dedupeFormKey(desired: string, taken: Set<string>): string {
  const base = desired.trim() || 'form'
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}

// ─── Import planning (preview) ───────────────────────────────────────────────

export interface PlannedForm {
  groupTitle: LocalizedString
  originalKey: string
  finalKey: string
  renamed: boolean
  title: LocalizedString
  definitionSource: DefinitionSource
  fieldCount: number
}

export interface ImportPlan {
  scope: ExportScope
  groupCount: number
  formCount: number
  forms: PlannedForm[]
  /** form_keys that collided with the target project and were renamed. */
  renamedCount: number
}

/**
 * Simulate the import without touching the DB, so the dialog can preview what
 * will be created and warn about renamed keys. `existingFormKeys` are the
 * form_keys already present in the *target* project (form_key is unique per
 * project). Dedup also accounts for collisions *within* the same envelope.
 */
export function planImport(
  envelope: FormExportEnvelope,
  existingFormKeys: string[],
): ImportPlan {
  const taken = new Set(existingFormKeys)
  const forms: PlannedForm[] = []

  for (const g of envelope.groups) {
    for (const f of g.forms) {
      const finalKey = dedupeFormKey(f.form_key, taken)
      taken.add(finalKey)
      forms.push({
        groupTitle: g.title,
        originalKey: f.form_key,
        finalKey,
        renamed: finalKey !== f.form_key,
        title: f.title,
        definitionSource: f.definition_source,
        fieldCount: Array.isArray(f.definition?.fields) ? f.definition.fields.length : 0,
      })
    }
  }

  return {
    scope: envelope.scope,
    groupCount: envelope.groups.length,
    formCount: forms.length,
    forms,
    renamedCount: forms.filter((f) => f.renamed).length,
  }
}

// ─── Misc helpers ─────────────────────────────────────────────────────────────

/** `bluedatalake-{slug}-{scope}-{YYYYMMDD}.json` */
export function exportFilename(projectTitle: string | undefined, scope: ExportScope): string {
  const slug =
    (projectTitle ?? 'project')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 32) || 'project'
  const d = new Date()
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate(),
  ).padStart(2, '0')}`
  return `bluedatalake-${slug}-${scope}-${stamp}.json`
}
